import type { QuoteBudgetRange, QuoteProjectType, QuoteTimeline } from '@mbbe/types';

export interface QuoteFeatureOption {
  label: string;
  emoji: string;
}

export const projectTypes: Array<{ value: QuoteProjectType; label: string; description: string }> = [
  { value: 'web', label: 'Web Projesi', description: 'Site, panel, e-ticaret veya web uygulaması' },
  { value: 'mobile', label: 'Mobil Uygulama', description: 'iOS, Android veya çapraz platform uygulama' },
  { value: 'desktop', label: 'Masaüstü Uygulama', description: 'Windows, macOS veya şirket içi araç' },
];

const withEmoji = (items: Array<[string, string]>): QuoteFeatureOption[] =>
  items.map(([emoji, label]) => ({ emoji, label }));

export const featureOptions: Record<QuoteProjectType, QuoteFeatureOption[]> = {
  web: withEmoji([
    ['🏢', 'Kurumsal web sitesi'],
    ['🎯', 'Landing page / tanıtım sayfası'],
    ['📄', 'Çok sayfalı web sitesi'],
    ['🌐', 'Web uygulaması'],
    ['🛠️', 'Admin panel / yönetim paneli'],
    ['👤', 'Müşteri paneli'],
    ['🤝', 'Bayi / firma paneli'],
    ['🔐', 'Kullanıcı giriş ve kayıt sistemi'],
    ['🧩', 'Rol bazlı yetkilendirme'],
    ['📝', 'İçerik yönetim sistemi'],
    ['📰', 'Blog / haber / duyuru yönetimi'],
    ['📦', 'Ürün listeleme sistemi'],
    ['🛒', 'E-ticaret altyapısı'],
    ['🏬', 'B2B sipariş sistemi'],
    ['🧾', 'Sepet ve sipariş yönetimi'],
    ['💳', 'Online ödeme entegrasyonu'],
    ['🔁', 'Abonelik / üyelik sistemi'],
    ['🏷️', 'Kampanya, kupon ve indirim sistemi'],
    ['📋', 'Form ve başvuru sistemi'],
    ['📅', 'Randevu / rezervasyon sistemi'],
    ['🗓️', 'Takvim ve planlama modülü'],
    ['📎', 'Dosya yükleme sistemi'],
    ['🖼️', 'Fotoğraf / video / medya yönetimi'],
    ['🔎', 'Gelişmiş arama ve filtreleme'],
    ['📍', 'Harita ve konum entegrasyonu'],
    ['🌍', 'Çoklu dil desteği'],
    ['🔔', 'Bildirim sistemi'],
    ['✉️', 'E-posta / SMS entegrasyonu'],
    ['🔌', '3. parti API entegrasyonları'],
    ['🤖', 'Yapay zeka entegrasyonu'],
    ['📊', 'Raporlama ve analiz ekranları'],
    ['📈', 'Dashboard / istatistik paneli'],
    ['⚡', 'Gerçek zamanlı veri takibi'],
    ['🔴', 'WebSocket / SignalR tabanlı canlı sistem'],
    ['🛡️', 'Güvenlik ve veri koruma'],
    ['🚀', 'Performans optimizasyonu'],
    ['🔍', 'SEO uyumlu yapı'],
    ['📱', 'Mobil uyumlu responsive tasarım'],
    ['➕', 'Mevcut web sitesine özellik ekleme'],
    ['🩹', 'Mevcut web sitesindeki hataları düzeltme'],
    ['📲', 'Web sitesini mobil uygulamaya dönüştürme'],
    ['☁️', 'Yayına alma / sunucu kurulumu'],
    ['💬', 'Teknik danışmanlık'],
    ['✨', 'Diğer'],
  ]),
  mobile: withEmoji([
    ['🍎', 'iOS mobil uygulama'],
    ['🤖', 'Android mobil uygulama'],
    ['📱', 'Cross-platform mobil uygulama'],
    ['🆕', 'Sıfırdan mobil uygulama geliştirme'],
    ['➕', 'Mevcut mobil uygulamaya özellik ekleme'],
    ['🩹', 'Mevcut mobil uygulamadaki hataları düzeltme'],
    ['🔁', 'Web sitesini mobil uygulamaya çevirme'],
    ['🎨', 'Mobil uygulama tasarım düzenlemesi'],
    ['🔐', 'Kullanıcı giriş ve kayıt sistemi'],
    ['🌐', 'Sosyal medya ile giriş'],
    ['🧩', 'Rol bazlı kullanıcı yönetimi'],
    ['🛠️', 'Admin destekli mobil yapı'],
    ['🔌', 'Backend ve API bağlantısı'],
    ['🔥', 'Firebase entegrasyonu'],
    ['🔔', 'Push notification'],
    ['📣', 'Uygulama içi bildirimler'],
    ['💳', 'Online ödeme entegrasyonu'],
    ['🔁', 'Abonelik / üyelik sistemi'],
    ['🏷️', 'Kampanya, kupon ve puan sistemi'],
    ['▦', 'QR kod tarama'],
    ['🏷️', 'Barkod tarama'],
    ['📷', 'Kamera kullanımı'],
    ['🖼️', 'Galeri / medya erişimi'],
    ['🎥', 'Fotoğraf ve video yükleme'],
    ['📎', 'Dosya yükleme'],
    ['📍', 'Harita ve konum servisleri'],
    ['🛰️', 'GPS tabanlı özellikler'],
    ['📡', 'Canlı konum takibi'],
    ['💬', 'Uygulama içi mesajlaşma'],
    ['📞', 'Sesli arama altyapısı'],
    ['🎦', 'Görüntülü arama altyapısı'],
    ['⚡', 'Gerçek zamanlı veri akışı'],
    ['🔴', 'WebSocket / SignalR bağlantısı'],
    ['📴', 'Offline çalışma modu'],
    ['🔄', 'Veri senkronizasyonu'],
    ['🌍', 'Çoklu dil desteği'],
    ['🔎', 'Gelişmiş arama ve filtreleme'],
    ['🗂️', 'Kategori sistemi'],
    ['📅', 'Randevu / rezervasyon sistemi'],
    ['🗓️', 'Takvim ve planlama modülü'],
    ['📊', 'Raporlama ve analiz ekranları'],
    ['🤖', 'Yapay zeka entegrasyonu'],
    ['🎮', 'Oyunlaştırma sistemi'],
    ['🏅', 'Puan, rozet, seviye sistemi'],
    ['🚀', 'Mobil performans optimizasyonu'],
    ['🩺', 'Çökme / donma sorunlarını giderme'],
    ['🍎', 'App Store yayınlama'],
    ['▶️', 'Google Play yayınlama'],
    ['🧾', 'Mağaza red sebebi analizi'],
    ['🖌️', 'Uygulama ikon ve splash screen hazırlığı'],
    ['📦', 'Build alma / sürüm çıkarma'],
    ['💬', 'Teknik danışmanlık'],
    ['✨', 'Diğer'],
  ]),
  desktop: withEmoji([
    ['🪟', 'Windows masaüstü uygulaması'],
    ['🍎', 'Mac masaüstü uygulaması'],
    ['🐧', 'Linux masaüstü uygulaması'],
    ['🏭', 'İşletmeye özel masaüstü yazılım'],
    ['🏢', 'ERP benzeri yönetim sistemi'],
    ['👥', 'CRM benzeri müşteri takip sistemi'],
    ['📦', 'Stok takip sistemi'],
    ['🧾', 'Sipariş yönetim sistemi'],
    ['🏷️', 'Ürün yönetim sistemi'],
    ['👤', 'Cari / müşteri yönetimi'],
    ['🪪', 'Personel yönetimi'],
    ['✅', 'Görev ve süreç takip sistemi'],
    ['📁', 'Başvuru ve belge takip sistemi'],
    ['📅', 'Randevu / rezervasyon yönetimi'],
    ['🧮', 'Muhasebe destekli işlem ekranları'],
    ['🏷️', 'Barkod okuyucu entegrasyonu'],
    ['▦', 'QR kod okuyucu entegrasyonu'],
    ['🖨️', 'Yazıcı / fiş / etiket çıktısı'],
    ['📂', 'Dosya ve belge yönetimi'],
    ['📊', 'Excel içe aktarma / dışa aktarma'],
    ['📄', 'PDF rapor oluşturma'],
    ['📈', 'Raporlama ve analiz ekranları'],
    ['📊', 'Dashboard / yönetim ekranı'],
    ['🔐', 'Kullanıcı giriş sistemi'],
    ['🧩', 'Rol bazlı yetkilendirme'],
    ['💾', 'Yerel veritabanı kullanımı'],
    ['☁️', 'Bulut veritabanı bağlantısı'],
    ['🔌', 'API bağlantısı'],
    ['🌐', 'Web panel ile senkron çalışma'],
    ['📱', 'Mobil uygulama ile senkron çalışma'],
    ['📶', 'Online / offline çalışma modu'],
    ['🗄️', 'Veri yedekleme sistemi'],
    ['🔄', 'Veri senkronizasyonu'],
    ['⚡', 'Gerçek zamanlı veri takibi'],
    ['🔔', 'Bildirim sistemi'],
    ['✉️', 'E-posta / SMS entegrasyonu'],
    ['🔗', '3. parti servis entegrasyonları'],
    ['🤖', 'Yapay zeka destekli işlem modülleri'],
    ['🔎', 'Gelişmiş arama ve filtreleme'],
    ['🌍', 'Çoklu dil desteği'],
    ['🛡️', 'Güvenlik ve oturum yönetimi'],
    ['🚀', 'Performans iyileştirme'],
    ['➕', 'Mevcut masaüstü yazılıma özellik ekleme'],
    ['🩹', 'Mevcut masaüstü yazılım hatalarını düzeltme'],
    ['📦', 'Kurulum dosyası hazırlama'],
    ['🔁', 'Versiyon / güncelleme sistemi'],
    ['💬', 'Teknik danışmanlık'],
    ['✨', 'Diğer'],
  ]),
};

export const budgetOptions: Array<{ value: QuoteBudgetRange; label: string }> = [
  { value: 'try_15000_35000', label: '₺15.000 - ₺35.000' },
  { value: 'try_35000_75000', label: '₺35.000 - ₺75.000' },
  { value: 'try_75000_plus', label: '₺75.000+' },
  { value: 'enterprise', label: 'Kurumsal teklif istiyorum' },
];

export const timelineOptions: Array<{ value: QuoteTimeline; label: string }> = [
  { value: 'now', label: 'Hemen başlamak istiyorum' },
  { value: 'within_1_month', label: '1 ay içinde' },
  { value: 'within_1_3_months', label: '1-3 ay içinde' },
];
